import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cc_profile")) || null; } catch (e) {
      console.warn("[auth] failed to parse stored profile", e);
      return null;
    }
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // 401 is expected when no session — only log unexpected failures
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[auth] /auth/me failed", e.response.status);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip /me check if OAuth callback in progress
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const setToken = useCallback((t) => {
    if (t) localStorage.setItem("cc_token", t);
    else localStorage.removeItem("cc_token");
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [setToken]);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [setToken]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {
      console.warn("[auth] logout request failed (ignored)", e?.response?.status);
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem("cc_profile");
    setProfile(null);
  }, [setToken]);

  const pickProfile = useCallback((p) => {
    setProfile(p);
    localStorage.setItem("cc_profile", JSON.stringify(p));
  }, []);

  // Memoize the provider value so consumers don't re-render every parent tick.
  const value = useMemo(
    () => ({ user, setUser, loading, login, signup, logout, refresh, profile, pickProfile, setToken }),
    [user, loading, login, signup, logout, refresh, profile, pickProfile, setToken],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
